// Inline next-step nudges (Phase 5 of the IA rethink).
//
// When an admin or manager creates an entity, we surface a small dismissible
// banner on the list page they return to, pointing them to the natural next
// step in the chain (Locations → Asset Types → Forms → Assets → Schedules).
//
// State lives in sessionStorage — a flag is written by the create handler
// and read by the list page on next render. The banner dismisses itself
// when clicked or when the user taps the X. Flags auto-expire so a stale
// nudge from yesterday doesn't haunt a long session.

const FLAG_PREFIX = 'cnt:nudge:';
// 30 minutes — long enough that a slow create-then-navigate flow still
// catches it, short enough that nudges from earlier sessions don't appear
// after returning to the same browser hours later.
const TTL_MS = 30 * 60 * 1000;

export type NudgeKey = 'location' | 'assetType' | 'asset' | 'form' | 'schedule';

interface NudgeContent {
  // Confirmation phrase — what just happened ("Location added.").
  message: string;
  // Verb-led label for the action button.
  actionLabel: string;
  // Where the button takes them — the next step in the chain.
  actionTo: string;
}

// Single source of truth for the chain. Industry-agnostic copy across the
// board — describes the concept, not a vertical-specific example.
export const NUDGES: Record<NudgeKey, NudgeContent> = {
  location: {
    message: 'Location added.',
    actionLabel: 'Add the assets that belong here',
    actionTo: '/assets/new',
  },
  assetType: {
    message: 'Asset type defined.',
    actionLabel: 'Now add specific assets that match it',
    actionTo: '/assets/new',
  },
  asset: {
    message: 'Asset added.',
    actionLabel: 'Schedule a check on it',
    actionTo: '/schedules',
  },
  form: {
    message: 'Form ready.',
    actionLabel: 'Set up a schedule that uses it',
    actionTo: '/schedules',
  },
  schedule: {
    message: 'Schedule set up.',
    actionLabel: 'View it on the Calendar',
    actionTo: '/calendar',
  },
};

export function markCreated(key: NudgeKey): void {
  try {
    window.sessionStorage.setItem(FLAG_PREFIX + key, String(Date.now()));
  } catch {
    // sessionStorage may be unavailable (private mode, quota) — silently
    // skip; the user just doesn't see the nudge.
  }
}

export function readNudge(key: NudgeKey): NudgeContent | null {
  try {
    const raw = window.sessionStorage.getItem(FLAG_PREFIX + key);
    if (!raw) return null;
    const ts = Number(raw);
    if (!Number.isFinite(ts) || Date.now() - ts > TTL_MS) {
      window.sessionStorage.removeItem(FLAG_PREFIX + key);
      return null;
    }
    return NUDGES[key];
  } catch {
    return null;
  }
}

export function clearNudge(key: NudgeKey): void {
  try {
    window.sessionStorage.removeItem(FLAG_PREFIX + key);
  } catch {
    // ignore
  }
}
